import { FaFileCsv, FaFilePdf, FaFileExcel } from "react-icons/fa6";
import { useState, useEffect } from "react";

// Hold to continue duration in milliseconds
const HOLD_DURATION_MS = 3000;

const Button = ({
  variant,
  loading,
  disabled,
  children,
  size,
  width = null,
  success = false,
  naked = false,
  holdToContinue = false,
  firstClickAction,
  onClick,
  additionalClasses = null,
  ...rest
}) => {
  const [holdCountdown, setHoldCountdown] = useState(0);
  const [holdProgress, setHoldProgress] = useState(0); // For smooth animation (0-5000ms)
  const [isHolding, setIsHolding] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [holdInterval, setHoldInterval] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    // Detect if device supports touch
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    
    return () => {
      if (holdInterval) {
        clearInterval(holdInterval);
      }
    };
  }, [holdInterval]);

  const handleClick = async (e) => {
    if (holdToContinue && !isActivated) {
      // First click - run firstClickAction if provided
      if (firstClickAction) {
        try {
          const shouldContinue = await firstClickAction(e);
          if (!shouldContinue) {
            return; // Don't proceed to hold state
          }
        } catch (error) {
          return; // Don't proceed if firstClickAction throws
        }
      }
      
      // Show hold message
      setIsActivated(true);
      setHoldCountdown(Math.ceil(HOLD_DURATION_MS / 1000));
      setHoldProgress(0);
      return;
    }
    
    if (!holdToContinue && onClick) {
      onClick(e);
    }
  };

  const startHoldCountdown = (e) => {
    if (holdToContinue && isActivated && !isHolding) {
      // Start countdown
      setIsHolding(true);
      setHoldProgress(0);
      
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remainingMs = Math.max(0, HOLD_DURATION_MS - elapsed);
        const remainingSeconds = Math.ceil(remainingMs / 1000);
        
        setHoldProgress(elapsed);
        setHoldCountdown(remainingSeconds);
        
        if (remainingMs <= 0) {
          // Countdown complete - execute action
          setIsActivated(false);
          setIsHolding(false);
          setHoldCountdown(0);
          setHoldProgress(0);
          clearInterval(interval);
          setHoldInterval(null);
          if (onClick) {
            onClick(e);
          }
        }
      }, 50); // Update every 50ms for smooth animation
      
      setHoldInterval(interval);
    }
  };

  const handleMouseDown = (e) => {
    // Only handle mouse events on non-touch devices
    if (!isTouchDevice) {
      startHoldCountdown(e);
    }
  };

  const stopHoldCountdown = () => {
    if (holdToContinue && isActivated && isHolding) {
      // User released during countdown - reset timer
      setIsHolding(false);
      setHoldCountdown(Math.ceil(HOLD_DURATION_MS / 1000));
      setHoldProgress(0);
      if (holdInterval) {
        clearInterval(holdInterval);
        setHoldInterval(null);
      }
    }
  };

  const handleMouseUp = () => {
    // Only handle mouse events on non-touch devices
    if (!isTouchDevice) {
      stopHoldCountdown();
    }
  };

  const resetHoldState = () => {
    if (holdToContinue && isActivated) {
      // Reset completely
      setIsActivated(false);
      setIsHolding(false);
      setHoldCountdown(0);
      setHoldProgress(0);
      if (holdInterval) {
        clearInterval(holdInterval);
        setHoldInterval(null);
      }
    }
  };

  const handleMouseLeave = () => {
    // Only handle mouse events on non-touch devices
    if (!isTouchDevice) {
      resetHoldState();
    }
  };

  // Touch event handlers for mobile devices
  const handleTouchStart = (e) => {
    // Prevent default to avoid mouse events firing on touch devices
    e.preventDefault();
    if (isTouchDevice) {
      startHoldCountdown(e);
    }
  };

  const handleTouchEnd = () => {
    if (isTouchDevice) {
      stopHoldCountdown();
    }
  };

  const handleTouchCancel = () => {
    if (isTouchDevice) {
      resetHoldState();
    }
  };
  let classes = ["relative btn" + (size ? "-" + size : "")];
  
  if (disabled && !loading && !success) {
    classes.push(
      "bg-theme-button-primary hover:bg-theme-button-primary text-theme-button-primary disabled:border-theme-button-disabled disabled:bg-theme-button-disabled disabled:text-theme-text-muted disabled:cursor-not-allowed shadow-none"
    );
  } else if (success || variant === "success") {
    classes.push("bg-emerald-500 hover:bg-emerald-600 text-white");
  } else {
    switch (variant) {
      case "primary":
        classes.push("btn-theme-primary");
        break;
      case "secondary":
        classes.push(
          "bg-theme-secondary border-theme-button-primary hover:bg-theme-button-secondary-hover  text-theme-button-secondary text-theme-primary hover:text-theme-button-primary-text hover:border-theme-border-secondary-hover dark:text-theme-button-primary-text"
        );
        break;
      case "tertiary":
        classes.push("border-theme-border-primary hover:border-theme-border-secondary text-theme-secondary");

        break;
      case "danger":
        classes.push("bg-rose-500 hover:bg-rose-600 text-white");
        break;
      case "secondary-danger":
        classes.push(
          "border-rose-500 hover:bg-rose-600 hover:text-white text-rose-500"
        );
        break;
      case "secondary-success":
        classes.push(
          "bg-theme-secondary border-theme-border-primary hover:border-theme-border-secondary text-emerald-500"
        );
        break;
      case "border":
        classes.push("border-theme-border-primary hover:border-theme-border-secondary text-theme-secondary");
        break;
    }
  }
  
  if (naked) {
    classes.push("!border-none shadow-none");
  }

  if (width === "full") {
    classes.push("w-full");
  }
  if( additionalClasses ){
    classes.push(additionalClasses);
  }

  const buttonDisabled = disabled || loading || success;
  const displayText = holdToContinue && isActivated ? `Hold for ${holdCountdown} seconds to ${children}` : children;
  
  // Calculate progress percentage for the fill animation (0 to 100%)
  const progressPercentage = holdToContinue && isActivated && isHolding 
    ? (holdProgress / HOLD_DURATION_MS) * 100 
    : 0;

  return (
    <button
      className={classes.join(" ")}
      disabled={buttonDisabled}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      {...rest}
    >
      {/* Progress bar overlay for hold-to-continue animation */}
      {holdToContinue && isActivated && isHolding && (
        <div 
          className="absolute inset-0 bg-gradient-to-r from-current to-current opacity-20 transition-all duration-100 ease-linear"
          style={{
            clipPath: `inset(0 ${100 - progressPercentage}% 0 0)`,
          }}
        />
      )}
      
      <span className={loading || success ? "relative opacity-25" : "relative z-10"}>
        {displayText}
      </span>
      {loading && (
        <span className="absolute z-20">
          <svg
            className="animate-spin w-4 h-4 fill-current shrink-0"
            viewBox="0 0 16 16"
          >
            <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
          </svg>
        </span>
      )}
      {success && (
        <span className={"absolute z-20"}>
          <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.707 7.293a1 1 0 00-1.414-1.414L9 12.586l-2.293-2.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l5-5z"
            />
          </svg>
        </span>
      )}
    </button>
  );
};

export const PrimaryButton = (props) => <Button variant="primary" {...props} />;
export const SecondaryButton = (props) => (
  <Button variant="secondary" {...props} />
);
export const TertiaryButton = (props) => (
  <Button variant="tertiary" {...props} />
);
export const DangerButton = (props) => <Button variant="danger" {...props} />;
export const SecondaryDangerButton = (props) => (
  <Button variant="secondary-danger" {...props} />
);
export const SuccessButton = (props) => <Button variant="success" {...props} />;
export const SecondarySuccessButton = (props) => (
  <Button variant="secondary-success" {...props} />
);
export const NakedButton = (props) => <Button variant="naked" {...props} />;
export const BorderButton = (props) => <Button variant="border" {...props} />;

// Generic colored buttons
export const GreenButton = (props) => (
  <Button 
    variant="success" 
    size="xs"
    className="relative group inline-flex  px-3 py-1.5 rounded-full text-sm font-medium transition-colors bg-green-500 hover:bg-green-600 text-white text-xs font-medium px-3 py-1"
    {...props} 
  />
);
export const RedButton = (props) => (
  <Button 
    variant="danger" 
    size="xs"
    className=" relative group inline-flex  px-3 py-1.5 rounded-full text-sm font-medium transition-colors bg-red-500 hover:bg-red-600 text-white text-xs font-medium px-3 py-1"
    {...props} 
  />
);

export function DownloadButton({
  type, // 'csv', 'pdf', or 'xlsx'
  onClick,
  loading,
  success,
  disabled,
}) {
  const isCSV = type === "csv";
  const isXlsx = type === "xlsx";
  const Icon = isCSV ? FaFileCsv : isXlsx ? FaFileExcel : FaFilePdf;

  const base =
    "relative group inline-flex  px-3 py-1.5 rounded-full text-sm font-medium transition-colors";
  const loadingStyle =
    "cursor-not-allowed opacity-30";
  const successStyle = "bg-emerald-500 hover:bg-emerald-600 text-white";
  const csvStyle =
    "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800";
  const pdfStyle =
    "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-800";
  const xlsxStyle =
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-800";

  const classes = [base];
  if ((loading || disabled) && !success) {
    classes.push(loadingStyle);
  } else if (success) {
    classes.push(successStyle);
  } else if (isXlsx) {
    classes.push(xlsxStyle);
  } else {
    classes.push(isCSV ? csvStyle : pdfStyle);
  }

  let contentClass = 'items-center gap-2 flex'
  if(loading || success){
    contentClass += " opacity-30"
  }
  return (
    <button
      disabled={loading || disabled}
      onClick={onClick}
      className={classes.join(" ")}
    >
      <div className={contentClass}>
        <Icon
          className={`transition-transform ${
            loading || success ? "opacity-30" : "group-hover:scale-110"
          }`}
        />
        {type.toUpperCase()}
      </div>


      {(loading || success) && (
        <span className="absolute inset-0 flex items-center justify-center">
          {success ? (
            <svg
              className="w-4 h-4 fill-current text-white"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.707 7.293a1 1 0 00-1.414-1.414L9 12.586l-2.293-2.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l5-5z"
              />
            </svg>
          ) : (
            <span className="absolute inset-0 flex items-center justify-center">
              <svg
                className="animate-spin w-4 h-4 fill-current shrink-0"
                viewBox="0 0 16 16"
              >
                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
              </svg>
            </span>
          )}
        </span>
      )}
    </button>
  );
}
